/**
 * Using TypedArray.subarray() is preferred
 * when accessing to data
 * to avoid extra memory allocation.
 *
 * Strange std::string behavior:
 * if std::string data length <=11, std::string object address is the same with its data;
 * if >11, std::string object name stores address of beginning of the data.
 * So in second case one can use WasmWrapper::CString method to get string bytes.
 * Maybe it's not related to data length, but to dynamic memory allocation.
 *
 *
 *
 * If memory is shared then memory growing is not allowed
 * and maximum memory size is specified at compilation.
 *
 *
 *
 * Passing argument by reference works as passing by pointer.
 * So, functions with reference parameters expect address instead of value.
 *
 *
 *
 * TODO: determination capabiity of what wasm memory type is being used.
 */



//  import Base from './base';



const IDLE_FUNCTION = () => 0;



const wasi_snapshot_preview1 =
{
	fd_seek: IDLE_FUNCTION,
	fd_write: IDLE_FUNCTION,
	fd_close: IDLE_FUNCTION,
	fd_fdstat_get: IDLE_FUNCTION,
	fd_advise: IDLE_FUNCTION,
	fd_allocate: IDLE_FUNCTION,
	fd_datasync: IDLE_FUNCTION,
	fd_fdstat_set_flags: IDLE_FUNCTION,
	fd_fdstat_set_rights: IDLE_FUNCTION,
	fd_filestat_get: IDLE_FUNCTION,
	fd_filestat_set_size: IDLE_FUNCTION,
	fd_filestat_set_times: IDLE_FUNCTION,
	fd_pread: IDLE_FUNCTION,
	fd_prestat_get: IDLE_FUNCTION,
	fd_prestat_dir_name: IDLE_FUNCTION,
	fd_pwrite: IDLE_FUNCTION,
	fd_read: IDLE_FUNCTION,
	fd_readdir: IDLE_FUNCTION,
	fd_renumber: IDLE_FUNCTION,
	fd_sync: IDLE_FUNCTION,
	fd_tell: IDLE_FUNCTION,

	path_create_directory: IDLE_FUNCTION,
	path_filestat_get: IDLE_FUNCTION,
	path_filestat_set_times: IDLE_FUNCTION,
	path_link: IDLE_FUNCTION,
	path_open: IDLE_FUNCTION,
	path_readlink: IDLE_FUNCTION,
	path_remove_directory: IDLE_FUNCTION,
	path_rename: IDLE_FUNCTION,
	path_symlink: IDLE_FUNCTION,
	path_unlink_file: IDLE_FUNCTION,
	poll_oneoff: IDLE_FUNCTION,
	proc_raise: IDLE_FUNCTION,
	sched_yield: IDLE_FUNCTION,
	random_get: IDLE_FUNCTION,
	sock_recv: IDLE_FUNCTION,
	sock_send: IDLE_FUNCTION,
	sock_shutdown: IDLE_FUNCTION,
	sock_accept: IDLE_FUNCTION,

	proc_exit: IDLE_FUNCTION,

	clock_time_get: IDLE_FUNCTION,

	args_get: IDLE_FUNCTION,
	args_sizes_get: IDLE_FUNCTION,
	environ_get: IDLE_FUNCTION,
	environ_sizes_get: IDLE_FUNCTION,
	clock_res_get: IDLE_FUNCTION,
};



export default class WasmWrapper
{
	static text_decoder = new TextDecoder('utf-8');
	static text_encoder = new TextEncoder();

	// static convertUint8ArrayToDomString (uint8_array)
	// {
	// 	return WasmWrapper.text_decoder.decode(uint8_array);
	// }

	// Version for shared buffer.
	// Decoding views of shared buffer is not allowed.
	static convertUint8ArrayToDomString (uint8_array)
	{
		return WasmWrapper.text_decoder.decode(uint8_array.slice());
	}

	static convertDomStringToUint8Array (text)
	{
		return WasmWrapper.text_encoder.encode(text);
	}

	constructor (options = {})
	{
		const wasm_wrapper = this;



		Object.assign
		(
			{
				usePreallocatedThreadStack: false,
				thread_count: 0,
				// 1024 b * 1024 b == 1048576 b == 1 mb
				thread_stack_size: 0x100000,
			},

			options,
		);

		this.options = options;



		this.ADDR_SIZE = 0;
		this.SIZE_SIZE = 0;
		this.UINT32_SIZE = 0;
		this.FLOAT_SIZE = 0;

		this.memory_views =
		{
			UI8: null,
			I8: null,
			UI16: null,
			I16: null,
			UI32: null,
			I32: null,
			// TODO: add 64 bit types.
			F32: null,
			F64: null,
		};



		this.Thread = class Thread
		{
			#needs_free;
			#promise;

			constructor (input, _data = [], stack_addr, js_loop)
			{
				if (wasm_wrapper.options.usePreallocatedThreadStack)
				{
					this.stack_index = wasm_wrapper.thread_stack_busy.indexOf(false);

					this.stack_addr = wasm_wrapper.thread_stack_base + (this.stack_index * wasm_wrapper.options.thread_stack_size);

					wasm_wrapper.thread_stack_busy[this.stack_index] = true;
				}
				else if (stack_addr)
				{
					this.stack_addr = stack_addr;
				}
				else
				{
					this.stack_addr = wasm_wrapper.exports.RDTY_WASM_WRAPPER_malloc(wasm_wrapper.options.thread_stack_size);

					this.#needs_free = true;
				}

				// Should be private.
				this.#promise = null;

				if (input instanceof Worker)
				{
					this.#promise =
						new Promise
						(
							(resolve) =>
							{
								this.worker = input;

								this.worker.onmessage = resolve;

								const { code, memory } = wasm_wrapper;

								this.worker.postMessage({ code, memory, _data });
							},
						);
				}
				else if (typeof input === 'string')
				{
					const call =
					(
						js_loop ?

							`setInterval(wasm_wrapper.exports.${ input }, 0, ..._data);` :

							`wasm_wrapper.exports.${ input }(..._data);`
					);

					this.#promise =
						new Promise
						(
							(resolve) =>
							{
								const blob_url =
									URL.createObjectURL
									(
										new Blob
										(
											[
												`${ wasm_wrapper.WasmWrapper_code }

												onmessage =
													async (message) =>
													{
														// const WasmWrapper =
														// 	(await import('/public/wasm-wrapper/thread.js')).default;

														const wasm_wrapper = new WasmWrapper();

														const { code, memory, _data } = message.data;

														await wasm_wrapper.init(code, memory, false);

														wasm_wrapper.exports.setStackPointer(${ this.stack_addr });

														${ call }

														postMessage(true);
													};`,
											],

											{ type: 'application/javascript' },
										),
									);

								this.worker = new Worker(blob_url);

								URL.revokeObjectURL(blob_url);

								this.worker.onmessage = resolve;

								const { code, memory } = wasm_wrapper;

								this.worker.postMessage({ code, memory, _data });
							},
						);
				}
				else if (typeof input === 'function')
				{
					const call = `(${ input })(..._data)`;

					this.#promise =
						new Promise
						(
							(resolve) =>
							{
								const blob_url =
									URL.createObjectURL
									(
										new Blob
										(
											[
												`${ wasm_wrapper.WasmWrapper_code }

												onmessage =
													async (message) =>
													{
														// const WasmWrapper =
														// 	(await import('/public/wasm-wrapper/thread.js')).default;

														const wasm_wrapper = new WasmWrapper();

														const { code, memory, _data } = message.data;

														await wasm_wrapper.init(code, memory, false);

														wasm_wrapper.exports.setStackPointer(${ this.stack_addr });

														${ call }

														postMessage(true);
													};`,
											],

											{ type: 'application/javascript' },
										),
									);

								this.worker = new Worker(blob_url);

								URL.revokeObjectURL(blob_url);

								this.worker.onmessage = resolve;

								const { code, memory } = wasm_wrapper;

								this.worker.postMessage({ code, memory, _data });
							},
						);
				}
			}

			terminate ()
			{
				this.worker.terminate();

				if (wasm_wrapper.options.usePreallocatedThreadStack)
				{
					wasm_wrapper.thread_stack_busy[this.stack_index] = false;
				}
				if (this.#needs_free)
				{
					wasm_wrapper.exports.RDTY_WASM_WRAPPER_free(this.stack_addr);
				}
			}

			async join ()
			{
				await this.#promise;

				this.terminate();
			}
		};
	}

	Char (addr, length = 1)
	{
		return this.memory_views.UI8.subarray(addr, addr + length);
	}

	Uint8 (addr, length = 1)
	{
		return this.memory_views.UI8.subarray(addr, addr + length);
	}

	Addr (addr, length = 1)
	{
		const _addr = addr / this.ADDR_SIZE;

		return this.memory_views.UI32.subarray(_addr, _addr + length);
	}

	Addr2 (name, length = 1)
	{
		return this.Addr(this.exports[name].value, length);
	}

	Uint32 (addr, length = 1)
	{
		const _addr = addr / this.UINT32_SIZE;

		return this.memory_views.UI32.subarray(_addr, _addr + length);
	}

	Size (addr, length = 1)
	{
		const _addr = addr / this.SIZE_SIZE;

		return this.memory_views.UI32.subarray(_addr, _addr + length);
	}

	Float (addr, length = 1)
	{
		const _addr = addr / this.FLOAT_SIZE;

		return this.memory_views.F32.subarray(_addr, _addr + length);
	}

	CStringLen (addr)
	{
		const _addr = addr;

		for (let vend = 0; ; ++vend)
		{
			if (this.Char(_addr + vend)[0] === 0)
			{
				return vend;
			}
		}
	}

	CString (addr)
	{
		return this.memory_views.UI8.subarray(addr, addr + this.CStringLen(addr));
	}

	CString2 (addr)
	{
		return WasmWrapper.convertUint8ArrayToDomString(this.memory_views.UI8.subarray(addr, addr + this.CStringLen(addr)));
	}

	StdString (addr)
	{
		/**
		 * 	These funcions must be defined:

		 *	extern "C" void* RDTY_WASM_WRAPPER_getStdStringData (std::string& s)
		 *	{
		 *		return s.data();
		 *	}
		 *
		 *	extern "C" size_t RDTY_WASM_WRAPPER_getStdStringSize (std::string& s)
		 *	{
		 *		return s.size();
		 *	}
		 */

		const result =
			this.Char
			(
				this.exports.RDTY_WASM_WRAPPER_getStdStringData(addr),

				this.exports.RDTY_WASM_WRAPPER_getStdStringSize(addr),
			);

		return result;
	}

	/**
	 * 	These funcions must be defined:

	 *	extern "C" void* RDTY_WASM_WRAPPER_getStdVectorData (std::vector<int>& v)
	 *	{
	 *		return v.data();
	 *	}
	 *
	 *	extern "C" size_t RDTY_WASM_WRAPPER_getStdVectorSize (std::vector<int>& v)
	 *	{
	 *		return v.size();
	 *	}
	 */

	StdVector (addr, type)
	{
		const result =
			this[type]
			(
				this.exports.RDTY_WASM_WRAPPER_getStdVectorData(addr),

				this.exports.RDTY_WASM_WRAPPER_getStdVectorSize(addr),
			);

		return result;
	}

	StdVectorUint8 (addr)
	{
		const result =
			this.Uint8
			(
				this.exports.RDTY_WASM_WRAPPER_getStdVectorData(addr),

				this.exports.RDTY_WASM_WRAPPER_getStdVectorSize(addr),
			);

		return result;
	}

	StdVectorSize (addr)
	{
		const result =
			this.Size
			(
				this.exports.RDTY_WASM_WRAPPER_getStdVectorData(addr),

				this.exports.RDTY_WASM_WRAPPER_getStdVectorSize(addr),
			);

		return result;
	}

	StdVectorUint32 (addr)
	{
		const result =
			this.Uint32
			(
				this.exports.RDTY_WASM_WRAPPER_getStdVectorData(addr),

				this.exports.RDTY_WASM_WRAPPER_getStdVectorSize(addr),
			);

		return result;
	}

	StdVectorFloat (addr)
	{
		const result =
			this.Float
			(
				this.exports.RDTY_WASM_WRAPPER_getStdVectorData(addr),

				this.exports.RDTY_WASM_WRAPPER_getStdVectorSize(addr),
			);

		return result;
	}

	StdVectorAddr (addr)
	{
		const result =
			this.Addr
			(
				this.exports.RDTY_WASM_WRAPPER_getStdVectorData(addr),

				this.exports.RDTY_WASM_WRAPPER_getStdVectorSize(addr),
			);

		return result;
	}

	Class (name)
	{
		const wasm_wrapper = this;



		class _Class
		{
			static name = name;

			static overloaded = {};



			constructor (input)
			{
				if (typeof input === 'number')
				{
					this.addr = input;
				}
				else if (typeof input === 'string')
				{
					[ this.addr ] = wasm_wrapper.Addr2(input);
				}
			}
		}

		global.Object.keys(this.exports_demangled)
			.filter
			(
				(member_name) => member_name.includes(name),
			)
			.forEach
			(
				(member_name) =>
				{
					let member_name_trimmed = member_name.replace(`${ name }::`, '').replace(/\((.*)+/g, '');

					// Some objects may be exported twice from wasm. TODO: why does it occur?
					const wasm_object =
						wasm_wrapper.exports_demangled[member_name][0] || wasm_wrapper.exports_demangled[member_name];

					if (typeof wasm_object === 'function')
					{
						const parameters =
							member_name.match(/\((.*)+/g)?.[0].replace(/\(|( )|\)/g, '').split(',');

						if (parameters.length && parameters[0] === '')
						{
							parameters.length = 0;
						}

						// static function
						if (parameters.length === wasm_object.length)
						{
							if (_Class[member_name_trimmed])
							{
								if (!_Class.overloaded[member_name_trimmed])
								{
									_Class.overloaded[member_name_trimmed] = 0;
								}

								++_Class.overloaded[member_name_trimmed];

								member_name_trimmed += `_o${ _Class.overloaded[member_name_trimmed] }`;
							}

							_Class[member_name_trimmed] = wasm_object;
						}

						// non-static function
						else
						{
							if (_Class.prototype[member_name_trimmed])
							{
								if (!_Class.overloaded[member_name_trimmed])
								{
									_Class.overloaded[member_name_trimmed] = 0;
								}

								++_Class.overloaded[member_name_trimmed];

								member_name_trimmed += `_o${ _Class.overloaded[member_name_trimmed] }`;
							}

							// Non-wrapped
							_Class[`proto_${ member_name_trimmed }`] = wasm_object;

							_Class.prototype[member_name_trimmed] =
								function (...args)
								{
									return wasm_object(this.addr, ...args);
								};
						}
					}
					else if (typeof wasm_object === 'object')
					{
						_Class[member_name_trimmed] = wasm_object;
					}
				},
			);

		// Presume New() returns a pointer
		if (_Class.New)
		{
			_Class.NewWrapper = () => new _Class(_Class.New());
		}



		return _Class;
	}

	updateStdVectorData (addr, type, _data)
	{
		this.exports.RDTY_WASM_WRAPPER_StdVector_resize(addr, _data.length);

		this.StdVector(addr, type).set(_data);
	}

	// waitThread (Worker, _data)
	// {
	// 	const promise =
	// 		new Promise
	// 		(
	// 			(resolve) =>
	// 			{
	// 				const worker = new Worker();

	// 				worker.onmessage = (msg) =>
	// 				{
	// 					console.log(msg.data)

	// 					worker.terminate();

	// 					resolve();
	// 				};

	// 				worker.postMessage({ wasm_code: this.code, wasm_memory: this.memory, ..._data });
	// 			},
	// 		);

	// 	return promise;
	// }

	// waitThread2 (_function, _data)
	// {
	// 	const promise =
	// 		new Promise
	// 		(
	// 			(resolve) =>
	// 			{
	// 				const blob_url =
	// 					URL.createObjectURL
	// 					(
	// 						new Blob
	// 						(
	// 							[ _function ],

	// 							{ type: 'application/javascript' },
	// 						),
	// 					);

	// 				const worker = new Worker(blob_url);

	// 				URL.revokeObjectURL(blob_url);

	// 				worker.onmessage = () =>
	// 				{
	// 					worker.terminate();

	// 					resolve();
	// 				};

	// 				worker.postMessage({ wasm_code: this.code, wasm_memory: this.memory, _data });
	// 			},
	// 		);

	// 	return promise;
	// }

	// waitThread3 (function_name, _data)
	// {
	// 	const promise =
	// 		new Promise
	// 		(
	// 			(resolve) =>
	// 			{
	// 				const blob_url =
	// 					URL.createObjectURL
	// 					(
	// 						new Blob
	// 						(
	// 							[
	// 								`onmessage =
	// 									async (message) =>
	// 									{
	// 										const WasmWrapper =
	// 											(await import('http://localhost:8080/public/wasm-wrapper/index.js')).default;

	// 										const wasm_wrapper = new WasmWrapper();

	// 										const { wasm_code, wasm_memory, _data } = message.data;

	// 										await wasm_wrapper.init(wasm_code, wasm_memory, false);



	// 										wasm_wrapper.exports.${ function_name }(..._data);



	// 										postMessage(true);
	// 									};`,
	// 							],

	// 							{ type: 'application/javascript' },
	// 						),
	// 					);

	// 				const worker = new Worker(blob_url);

	// 				URL.revokeObjectURL(blob_url);

	// 				worker.onmessage = () =>
	// 				{
	// 					worker.terminate();

	// 					resolve();
	// 				};

	// 				const { code, memory } = this;

	// 				worker.postMessage({ code, memory, _data });
	// 			},
	// 		);

	// 	return promise;
	// }

	// // Why not working in workers? Because of malloc?
	// demangle (mangled_name)
	// {
	// 	const mangled_name_length = mangled_name.length;
	// 	const mangled_name_addr = this.exports.RDTY_WASM_WRAPPER_malloc(mangled_name_length);

	// 	this.memory_views.UI8.set(WasmWrapper.convertDomStringToUint8Array(mangled_name), mangled_name_addr);

	// 	const demangled_name =
	// 		WasmWrapper.convertUint8ArrayToDomString
	// 		(this.CString(this.exports.RDTY_WASM_WRAPPER_demangleCxxName(mangled_name_addr)));

	// 	this.exports.RDTY_WASM_WRAPPER_free(mangled_name_addr);

	// 	return demangled_name;
	// }

	demangle (name, name_addr)
	{
		this.memory_views.UI8.set(WasmWrapper.convertDomStringToUint8Array(name), name_addr);

		const demangled_name =
			WasmWrapper.convertUint8ArrayToDomString
			(this.CString(this.exports.RDTY_WASM_WRAPPER_demangleCxxName(name_addr)))
				.replace(/, /g, ',');

		return demangled_name;
	}

	async init (code, memory, demangle = true, env_imports = {})
	{
		this.code = code;
		this.memory = null;

		const wasm_module = await WebAssembly.compile(code);

		const wasm_module_imports = WebAssembly.Module.imports(wasm_module);

		if (memory)
		{
			this.memory = memory;
		}
		else
		{
			const [ imported_memory ] =
				wasm_module_imports
					.filter((import_desc) => (import_desc.kind === 'memory'));

			// chrome://flags#enable-experimental-webassembly-features must be enabled
			// to be able to specify shared memory size in WebAssembly module.
			if (imported_memory.type?.shared)
			{
				const memory_desc =
				{
					initial: imported_memory.type.maximum,
					maximum: imported_memory.type.maximum,
					shared: true,
				};

				this.memory = new WebAssembly.Memory(memory_desc);
			}
			else
			{
				this.memory =
					new WebAssembly.Memory
					({
						initial: 65536,
						maximum: 65536,
						shared: true,
					});
			}
		}

		env_imports =
			Object.assign
			(
				env_imports,

				{
					memory: this.memory,

					__multi3: IDLE_FUNCTION,
					__muloti4: IDLE_FUNCTION,
					__trunctfdf2: IDLE_FUNCTION,
					console_log: (x) => console.log('%cWASM:', 'color: #bada55', x),
					console_log_c: (x) => console.log('%cWASM:', 'color: #bada55', String.fromCharCode(x)),
					console_log_s: (x) => console.log('%cWASM:', 'color: #bada55', this.CString2(x)),
					console_log_f: (x) => console.log('%cWASM:', 'color: #bada55', x),
					date_now: () => Date.now(),
				},
			);

		console.log(wasm_module);

		wasm_module_imports
			.forEach
			(
				(_import) =>
				{
					if (_import.module === 'wasi_snapshot_preview1' && !wasi_snapshot_preview1[_import.name])
					{
						console.log('Missed wasi_snapshot_preview1 import:', _import.name);
					}

					if (_import.module === 'env' && !env_imports[_import.name])
					{
						console.log('Missed env import:', _import.name);
					}
				},
			);

		// this.module = wasm_module;

		const wasm_module_instance =
			await WebAssembly.instantiate
			// await WebAssembly.instantiateStreaming
			(
				wasm_module,

				{
					env: env_imports,

					// TODO: learn what is wasi_snapshot_preview1.
					wasi_snapshot_preview1,
				},
			);

		console.log(wasm_module_instance);

		this.instance = wasm_module_instance;
		this.exports = wasm_module_instance.exports;

		this.ADDR_SIZE = this.exports.RDTY_WASM_WRAPPER_sizeof(0);
		this.SIZE_SIZE = this.exports.RDTY_WASM_WRAPPER_sizeof(1);
		this.UINT32_SIZE = this.exports.RDTY_WASM_WRAPPER_sizeof(2);
		this.FLOAT_SIZE = this.exports.RDTY_WASM_WRAPPER_sizeof(3);

		// imported || exported
		const { buffer } = this.memory || wasm_module_instance.exports.memory;

		this.memory_views.UI8 = new Uint8Array(buffer);
		this.memory_views.I8 = new Int8Array(buffer);
		this.memory_views.UI16 = new Uint16Array(buffer);
		this.memory_views.I16 = new Int16Array(buffer);
		this.memory_views.UI32 = new Uint32Array(buffer);
		this.memory_views.I32 = new Int32Array(buffer);
		this.memory_views.F32 = new Float32Array(buffer);
		this.memory_views.F64 = new Float64Array(buffer);



		if (demangle)
		{
			const demangled_name_max_length = 1024;
			const demangled_name_addr = this.exports.RDTY_WASM_WRAPPER_malloc(demangled_name_max_length);

			this.exports_demangled =
				Object.keys(this.exports)
					.reduce
					(
						(exports_demangled, _name) =>
						{
							if (_name.startsWith('_Z'))
							{
								const name = `${ _name }\0`;

								const demangled_name = this.demangle(name, demangled_name_addr);

								if (exports_demangled[demangled_name])
								{
									if (Array.isArray(exports_demangled[demangled_name]))
									{
										exports_demangled[demangled_name].push(this.exports[_name]);

										return exports_demangled;
									}

									const first_entry = exports_demangled[demangled_name];

									exports_demangled[demangled_name] = [ first_entry, this.exports[_name] ];

									return exports_demangled;
								}

								exports_demangled[demangled_name] = this.exports[_name];
							}

							return exports_demangled;
						},

						{},
					);

			this.exports.RDTY_WASM_WRAPPER_free(demangled_name_addr);

			console.log(this.exports_demangled);
		}



		if (this.options.usePreallocatedThreadStack)
		{
			this.thread_stack_busy = new Array(this.options.thread_count).fill(false);

			this.thread_stack_base = this.exports.RDTY_WASM_WRAPPER_malloc(this.options.thread_stack_size * this.options.thread_count);
		}



		this.WasmWrapper_code =
		(
			// await fetch('/public/wasm-wrapper/thread.js')
			await fetch(`${ window.location.href }public/wasm-wrapper/thread.js`)
				.then((response) => response.text())
		);
		// .replace('export default ', '');
	}
}
